import React, { useState } from 'react'
import { api } from '../../services/api'
import toast from 'react-hot-toast'

const DataExport = () => {
  const [exportType, setExportType] = useState('all_users')
  const [dateRangeType, setDateRangeType] = useState('last_30_days')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loading, setLoading] = useState(false)

  const handleExport = async () => {
    if (dateRangeType === 'custom' && (!startDate || !endDate)) {
      toast.error('Please select both start and end dates for custom range')
      return
    }

    try {
      setLoading(true)
      
      const params = {
        date_range_type: dateRangeType
      }
      
      if (dateRangeType === 'custom') {
        params.start_date = startDate
        params.end_date = endDate
      }
      
      let endpoint
      if (exportType === 'face_scan') {
        endpoint = '/admin/export/face-scan-reports'
      } else if (exportType === 'hra') {
        endpoint = '/admin/export/hra-data'
      } else {
        endpoint = '/admin/export/all-users'
      }
      
      const response = await api.get(endpoint, {
        params,
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename
      if (exportType === 'face_scan') {
        filename = `face_scan_reports_${dateRangeType}_${new Date().toISOString().split('T')[0]}.xlsx`
      } else if (exportType === 'hra') {
        filename = `hra_data_${dateRangeType}_${new Date().toISOString().split('T')[0]}.xlsx`
      } else {
        filename = `all_users_${dateRangeType}_${new Date().toISOString().split('T')[0]}.xlsx`
      }
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success('Data exported successfully!')
    } catch (error) {
      console.error('Error exporting data:', error)
      toast.error(error.response?.data?.detail || 'Failed to export data')
    } finally {
      setLoading(false)
    }
  }

  const formatDateForInput = (date) => {
    if (!date) return ''
    const d = new Date(date)
    return d.toISOString().split('T')[0]
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Export Data to Excel</h3>
      
      <div className="space-y-4">
        {/* Export Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data Type
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="all_users"
                checked={exportType === 'all_users'}
                onChange={(e) => setExportType(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">All Users (Including No Scans/HRAs)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="face_scan"
                checked={exportType === 'face_scan'}
                onChange={(e) => setExportType(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Face Scan Reports</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="hra"
                checked={exportType === 'hra'}
                onChange={(e) => setExportType(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">HRA Data</span>
            </label>
          </div>
        </div>

        {/* Date Range Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <select
            value={dateRangeType}
            onChange={(e) => setDateRangeType(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="custom">Select Date Range</option>
          </select>
        </div>

        {/* Custom Date Range */}
        {dateRangeType === 'custom' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={formatDateForInput(new Date())}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={formatDateForInput(new Date())}
                min={startDate}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {/* Export Button */}
        <div className="pt-4">
          <button
            onClick={handleExport}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DataExport

